import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { getEntries, blogFields } from 'lib/contentful-client';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import Component404 from 'components/consumer/404';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import useIsBrowser from 'hooks/useIsBrowser';
import ImageSlider from '../image-slider';

import ContentfulImage from './components/ContentfulImage/ContentfulImage';
import CloseButton from './components/CloseButton/CloseButton';
import Social from './components/Social/Social';
import BlockQuote from './components/BlockQuote/BlockQuote';
import JBLogo from './components/Icons/JBLogo/JBLogo';
import ProductsGridStatic from '../products-grid/ProductsGridStatic';

function generateSliderConfig(images) {
  const imageProperties = [];
  images.forEach(image => {
    imageProperties.push({
      src: `https:${image.fields.file.url}?h=346&w=346&fit=fill&fl=progressive`,
      alt: image.fields.title,
    });
  });
  return {
    images: imageProperties,
  };
}

const ProductsGridStaticWithData = ({ productIds }) => {
  const productIdsAsInts = productIds.map(id => parseInt(id, 10));
  const { loading, products } = useStaticProductCardData({
    productIds: productIdsAsInts,
  });

  return (
    <ProductsGridStatic
      columnsOnDesktop="4"
      columnsOnMobile="2"
      layout="mini"
      listName="blog"
      loading={loading}
      maxCardsCount={2}
      productsData={products}
    />
  );
};

const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: node => {
      const image =
        node.data.target.fields.file &&
        node.data.target.fields.file.contentType.includes('image/')
          ? node.data.target.fields.file
          : null;

      const description =
        node.data.target.fields?.description !== ''
          ? node.data.target.fields?.description
          : node.data.target.fields?.title !== ''
          ? node.data.target.fields?.title
          : '';

      if (image && image.url) {
        return (
          <ContentfulImage
            alt={description}
            type="blogProduct"
            src={`https:${image.url}`}
          />
        );
      }
      return null;
    },
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      const { images, embedCode, productIds } = node.data.target.fields;

      if (images) {
        const sliderConfig = generateSliderConfig(images);
        return (
          <div className="slider min-[1380px]:ml-[-340px] min-[1380px]:mr-[-340px]">
            <ImageSlider
              config={sliderConfig}
              hideArrowsIfAllSlidesVisible
              className="bg-transparent p-0 my-12"
            />
          </div>
        );
      }

      if (productIds && productIds.length > 0) {
        /* Product ids for testing
        const productIdstest = [
          8272,
          8272,
          9289,
          8272,
          9514,
          8272,
          6466,
          8272,
          9204,
          8415,
          623,
        ]; */
        return (
          <div className="min-[1200px]:mb-4">
            <ProductsGridStaticWithData productIds={productIds} />
          </div>
        );
      }

      if (embedCode && embedCode.includes('fast.wistia.com/embed/medias')) {
        let playerDiv = '';
        const scriptSrc = [];
        const scriptTag = embedCode.split('</script>');
        if (scriptTag.length > 0) {
          scriptTag.forEach(script => {
            const src = /<script.*?src="(.*?)"/g.exec(script);
            if (src && src.length > 1 && !scriptSrc.includes(src[1])) {
              scriptSrc.push(src[1]);
            } else {
              playerDiv = script;
            }
          });
          return (
            <>
              <Helmet>
                {scriptSrc.map(items => (
                  <script src={items} async />
                ))}
              </Helmet>

              <div dangerouslySetInnerHTML={{ __html: playerDiv }} />
            </>
          );
        }
      } else if (embedCode && embedCode.includes('<script')) {
        const regex = /<script.*?src="(.*?)">.*?<\/script>/gim;
        const jsPaths = [];
        let embedHtml = embedCode;
        let m = regex.exec(embedHtml);

        while (m !== null) {
          if (m.index === regex.lastIndex) {
            regex.lastIndex += 1;
          }
          for (const [groupIndex, match] of m.entries()) {
            if (groupIndex === 0) {
              embedHtml = embedHtml.replace(new RegExp(match, 'g'), '');
            } else if (groupIndex === 1) {
              jsPaths.push(match);
            }
          }
          m = regex.exec(embedHtml);
        }
        if (jsPaths && jsPaths.length > 0) {
          return (
            <>
              <Helmet>
                {jsPaths.map(item => (
                  <script src={item} />
                ))}
              </Helmet>
              <div dangerouslySetInnerHTML={{ __html: embedHtml }} />
            </>
          );
        }
      } else if (embedCode) {
        return (
          <div
            className="p-[.5em] flex"
            dangerouslySetInnerHTML={{ __html: embedCode }}
          />
        );
      }
      return null;
    },
    [BLOCKS.HEADING_5]: (node, children) => (
      <h5 className="text-lg font-bold">{children}</h5>
    ),
    [BLOCKS.QUOTE]: (node, children) => <BlockQuote>{children}</BlockQuote>,
  },
};

const SinglePost = ({ blogUrl, slug, history, staticContext }) => {
  const [postDetails, setPostDetails] = useState(null);
  // future use
  const [categories, setCategories] = useState(null);
  const [featuredImage, setFeaturedImage] = useState({
    url: null,
    description: null,
  });
  const isBrowser = useIsBrowser();

  const fetchPost = useCallback(
    async postSlug => {
      if (!isBrowser) return;

      const entries = await getEntries(staticContext, {
        params: {
          'fields.slug': postSlug,
        },
      });
      if (entries.items && entries.items.length > 0) {
        const post = blogFields(entries, blogUrl);
        post[0].createdAt = entries.items[0].sys.createdAt;
        post[0].updatedAt = entries.items[0].sys.updatedAt;

        // future use
        setCategories(
          postDetails && postDetails.categories ? postDetails.categories[0] : ''
        );
        setPostDetails(post[0]);
        setFeaturedImage({
          url: post[0].featuredImage.fields?.file?.url ?? null,
          description: post[0].featuredImage.fields?.description ?? null,
        });
      } else {
        setPostDetails(false);
        setFeaturedImage({
          url: null,
          description: null,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slug, blogUrl, isBrowser]
  );

  useEffect(() => {
    fetchPost(slug);
  }, [slug, fetchPost]);

  if (postDetails === false) return <Component404 />;
  if (postDetails === null)
    return (
      <div className="text-center pt-10 bg-white">
        <ProgressLoader />
      </div>
    );

  const ogTitle = postDetails.seoTitle
    ? postDetails.seoTitle
    : postDetails.title;
  const description =
    postDetails.featuredImage.fields?.description !== ''
      ? postDetails.featuredImage.fields?.description
      : postDetails.featuredImage.fields?.title !== ''
      ? postDetails.featuredImage.fields?.title
      : '';

  return (
    <>
      <Helmet>
        <title>{`${postDetails.title} | Joybird`}</title>

        <meta name="description" content={postDetails.metaDescription} />
        <meta property="og:title" content={`${ogTitle} | Joybird`} />
        <meta property="og:description" content={postDetails.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="article:section" content={categories} />

        <meta
          property="article:published_time"
          content={postDetails.createdAt}
        />

        <meta
          property="article:modified_time"
          content={postDetails.updatedAt}
        />

        <meta property="article:updated_time" content={postDetails.updatedAt} />

        <meta
          property="og:image"
          content={`https:${postDetails.featuredImage.fields.file.url}`}
        />

        <meta
          property="og:image:secure_url"
          content={`https:${postDetails.featuredImage.fields.file.url}`}
        />
      </Helmet>

      <div className="block">
        <ContentfulImage
          alt={description}
          src={featuredImage.url ? `https:${featuredImage?.url}` : ''}
          type="hero"
          className="block w-full h-full max-w-[1380px] object-cover mx-auto my-0"
        />
        <CloseButton onClick={() => history.push(`/${blogUrl}`)} />
      </div>
      <div className="layout">
        <div className="w-full max-w-[650px] mx-auto my-[5em] pt-[1em] text-center relative before:[content:''] before:w-10 before:h-[3px] before:bg-[#6bb7bf] before:absolute before:top-0 before:left-1/2 before:-ml-5">
          <h1 className="text-3xl font-bold">{postDetails.title}</h1>
          <p className="mx-0 my-[1.5em]">{postDetails.description}</p>
          <Social />
        </div>

        <div className="w-full mx-auto my-0 pl-5 pr-5 md:max-w-[700px] md:p-0 [&_.lazyloaded]:mx-0 [&_.lazyloaded]:my-4 [&_a]:text-brand [&_a:focus-visible]:focus-visible:underline [&_a:hover]:underline [&_p]:mb-[1.5em] [&_p]:tracking-[-.15px] [&_p]:text-lg [&_p]:leading-[1.45rem] [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mx-0 [&_h2]:mb-0 [&_h2]:tracking-[-.05rem] [&_h3]:mt-12 [&_h3]:mx-0 [&_h3]:mb-0 [&_h3]:tracking-[-.025rem] [&_h3]:font-bold [&_h5]:mb-6">
          {documentToReactComponents(postDetails.body, options)}
          <div className="text-center px-0 py-[70px]">
            <JBLogo color="#6bb7bf" />
          </div>
        </div>
      </div>
    </>
  );
};

SinglePost.propTypes = {
  blogUrl: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  history: PropTypes.objectOf(PropTypes.object).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.object).isRequired,
};

ProductsGridStaticWithData.propTypes = {
  productIds: PropTypes.array,
};

export default SinglePost;
